import {
  ChangeDetectorRef, Component, OnInit,
} from '@angular/core';
import { NotificationService } from '../../../application/services/NotificationService';
import { HttpResponse } from '../../../application/types/HttpResponse';
import { Client, ClientListResult } from '../../../clients/types/Client';
import { ClientApiService } from '../../../clients/services/client-api/ClientApiService';
import { EventApiService } from '../../../events/services/event-api/EventApiService';
import { SportsEvent } from '../../../events/types/Event';
import { OrderApiService } from '../../services/order-api/OrderApiService';
import {
  Order, PayOrderData, SaveOrderData, OrderStatus,
} from '../../types/Order';

type OrderSortField = 'code' | 'client' | 'event' | 'tickets' | 'total' | 'reservation' | 'status';
type SortDirection = 'asc' | 'desc';

@Component({
  selector: 'app-orders-page',
  standalone: false,
  templateUrl: './orders-page.component.html',
  styleUrl: './orders-page.component.sass',
})
export class OrdersPageComponent implements OnInit {
  public orders: Order[] = [];
  public clients: Client[] = [];
  public events: SportsEvent[] = [];
  public statusFilter: OrderStatus | '' = '';
  public page = 1;
  public pageSize = 20;
  public readonly pageSizeOptions = [20, 50, 100];
  public loading = false;
  public saving = false;
  public showCreateDialog = false;
  public errorMessage: string | null = null;
  public successMessage: string | null = null;
  public form: SaveOrderData = this.createEmptyForm();
  public sortField: OrderSortField = 'code';
  public sortDirection: SortDirection = 'desc';

  public constructor(
    private orderApi: OrderApiService,
    private clientApi: ClientApiService,
    private eventApi: EventApiService,
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    await this.loadOrders();
  }

  public get filteredOrders(): Order[] {
    return this.statusFilter ? this.orders.filter((order) => order.status === this.statusFilter) : this.orders;
  }

  public get pagedOrders(): Order[] {
    const startIndex = (this.page - 1) * this.pageSize;
    return this.sortedOrders.slice(startIndex, startIndex + this.pageSize);
  }

  public get sortedOrders(): Order[] {
    const direction = this.sortDirection === 'asc' ? 1 : -1;
    return [
      ...this.filteredOrders,
    ].sort((left, right) => this.compareByField(left, right, this.sortField) * direction);
  }

  public get totalPages(): number {
    return Math.max(Math.ceil(this.filteredOrders.length / this.pageSize), 1);
  }

  public get selectedEvent(): SportsEvent | null {
    return this.events.find((event) => event.id === this.form.eventId) ?? null;
  }

  public get availableTickets() {
    return this.selectedEvent?.tickets.filter((ticket) => ticket.status === 'available') ?? [];
  }

  public get selectedTotal(): number {
    return this.availableTickets
      .filter((ticket) => this.form.ticketIds.includes(ticket.id))
      .reduce((total, ticket) => total + ticket.salePrice, 0);
  }

  public async setStatusFilter(status: OrderStatus | ''): Promise<void> {
    this.statusFilter = status;
    this.page = 1;
    await Promise.resolve();
  }

  public setPageSize(pageSize: number): void {
    this.pageSize = pageSize;
    this.page = 1;
  }

  public goToPage(page: number): void {
    this.page = Math.min(Math.max(page, 1), this.totalPages);
  }

  public setSort(field: OrderSortField): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      return;
    }

    this.sortField = field;
    this.sortDirection = field === 'client' || field === 'event' || field === 'status' ? 'asc' : 'desc';
  }

  public sortState(field: OrderSortField): string {
    if (this.sortField !== field) {
      return 'none';
    }

    return this.sortDirection === 'asc' ? 'ascending' : 'descending';
  }

  public async openCreateDialog(): Promise<void> {
    this.errorMessage = null;
    this.form = this.createEmptyForm();
    this.showCreateDialog = true;
    await this.loadSelectionData();
  }

  public closeCreateDialog(): void {
    if (!this.saving) {
      this.showCreateDialog = false;
    }
  }

  public onEventChange(): void {
    this.form.ticketIds = [];
  }

  public toggleTicket(ticketId: string): void {
    this.form.ticketIds = this.form.ticketIds.includes(ticketId)
      ? this.form.ticketIds.filter((id) => id !== ticketId)
      : [...this.form.ticketIds, ticketId];
  }

  public isTicketSelected(ticketId: string): boolean {
    return this.form.ticketIds.includes(ticketId);
  }

  public async createOrder(): Promise<void> {
    if (!this.form.clientId || !this.form.eventId || this.form.ticketIds.length === 0) {
      this.errorMessage = 'Выберите клиента, событие и хотя бы один свободный билет.';
      return;
    }

    this.saving = true;
    this.errorMessage = null;
    try {
      const response = await this.orderApi.createOrder(this.form);
      if (response.status === 'failed') {
        this.errorMessage = response.errorMessage;
        return;
      }
      this.successMessage = `Заказ ${response.response.code} создан и переведен в резерв.`;
      this.notificationService.notify('order', 'Создан резерв билетов', `${response.response.code}: ${response.response.items.length} шт.`);
      this.showCreateDialog = false;
      await Promise.all([this.loadOrders(), this.loadEvents()]);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.saving = false;
      this.changeDetector.detectChanges();
    }
  }

  public async payOrder(order: Order): Promise<void> {
    const paymentReference = window.prompt('Укажите комментарий или номер платежа (необязательно):', '') ;
    if (paymentReference === null) {
      return;
    }
    const receivedByUserId = order.receivedByUserId ?? order.event?.participants[0]?.userId ?? '';
    if (!receivedByUserId) {
      this.errorMessage = 'Откройте заказ и выберите ответственного за продажу.';
      return;
    }

    await this.updateOrder(order, 'pay', {
      paymentReference: paymentReference.trim() || null,
      paymentMethod: order.paymentMethod ?? 'card',
      receivedByUserId,
    });
  }

  public async cancelOrder(order: Order): Promise<void> {
    if (!window.confirm(`Отменить ${order.code}? Билеты снова станут доступными.`)) {
      return;
    }

    await this.updateOrder(order, 'cancel');
  }

  public getStatusName(status: OrderStatus): string {
    const names: Record<OrderStatus, string> = {
      draft: 'Черновик',
      reserved: 'Резерв',
      paid: 'Оплачен',
      cancelled: 'Отменен',
      expired: 'Резерв истек',
      refunded: 'Возврат',
    };
    return names[status];
  }

  public formatMoney(value: number): string {
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(value);
  }

  public formatDate(value: string | null): string {
    if (!value) {
      return '—';
    }
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(new Date(value));
  }

  public formatEventDate(value: string | null): string {
    if (!value) {
      return '—';
    }
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC',
    }).format(new Date(value));
  }

  public formatEvent(event: SportsEvent): string {
    return `${event.homeTeam.name} - ${event.awayTeam.name}`;
  }

  public trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  public getOrderRoute(order: Order): string {
    return order.code || order.id;
  }

  private async updateOrder(order: Order, action: 'pay' | 'cancel', paymentData?: PayOrderData): Promise<void> {
    this.errorMessage = null;
    try {
      const response = action === 'pay'
        ? await this.orderApi.payOrder(order.id, paymentData as PayOrderData)
        : await this.orderApi.cancelOrder(order.id);
      if (response.status === 'failed') {
        this.errorMessage = response.errorMessage;
        return;
      }
      const actionName = action === 'pay' ? 'оплачен' : 'отменен';
      this.successMessage = `Заказ ${response.response.code} ${actionName}.`;
      this.notificationService.notify('order', `Заказ ${actionName}`, response.response.code);
      await Promise.all([this.loadOrders(), this.loadEvents()]);
    } catch (error) {
      this.handleUnknownError(error);
    }
  }

  private async loadOrders(): Promise<void> {
    this.loading = true;
    this.errorMessage = null;
    try {
      const response = await this.orderApi.getOrders();
      const result = this.unwrapResponse(response);
      if (result) {
        this.orders = result;
        this.page = Math.min(this.page, this.totalPages);
      }
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  private async loadSelectionData(): Promise<void> {
    await Promise.all([this.loadClients(), this.loadEvents()]);
    this.changeDetector.detectChanges();
  }

  private async loadClients(): Promise<void> {
    const response = await this.clientApi.getClients({ search: '', status: 'active', page: 1, pageSize: 100 });
    const result = this.unwrapResponse(response);
    if (result) {
      this.clients = (result as ClientListResult).clients;
    }
  }

  private async loadEvents(): Promise<void> {
    const response = await this.eventApi.getEvents();
    const result = this.unwrapResponse(response);
    if (result) {
      this.events = (result as SportsEvent[]).filter((event) => event.tickets.some((ticket) => ticket.status === 'available'));
    }
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }
    return response.response;
  }

  private createEmptyForm(): SaveOrderData {
    return {
      clientId: '',
      eventId: '',
      ticketIds: [],
      reservationMinutes: 60,
    };
  }

  private handleUnknownError(error: unknown): void {
    this.errorMessage = error instanceof Error ? error.message : 'Не удалось выполнить действие.';
  }

  private compareByField(left: Order, right: Order, field: OrderSortField): number {
    if (field === 'code') {
      return left.code.localeCompare(right.code, 'ru', { numeric: true });
    }
    if (field === 'client') {
      return (left.client?.name ?? '').localeCompare(right.client?.name ?? '', 'ru');
    }
    if (field === 'event') {
      return (left.event?.name ?? '').localeCompare(right.event?.name ?? '', 'ru');
    }
    if (field === 'tickets') {
      return left.items.length - right.items.length;
    }
    if (field === 'total') {
      return left.total - right.total;
    }
    if (field === 'reservation') {
      return this.getTimestamp(left.reservationExpiresAt) - this.getTimestamp(right.reservationExpiresAt);
    }

    return this.getStatusPriority(left.status) - this.getStatusPriority(right.status);
  }

  private getTimestamp(value: string | null): number {
    return value ? new Date(value).getTime() : 0;
  }

  private getStatusPriority(status: OrderStatus): number {
    const priorities: Record<OrderStatus, number> = {
      reserved: 0,
      paid: 1,
      cancelled: 2,
      expired: 3,
      refunded: 4,
      draft: 5,
    };

    return priorities[status];
  }
}
